import { useCallback, useState } from "react";

/**
 * @typedef {Object} UseMultiStepDialogReturn
 * @property {number} activeStep - Current zero-based step index.
 * @property {boolean} isFirstStep - True when on the first step.
 * @property {boolean} isLastStep - True when on the last step.
 * @property {Function} goToNext - Advance to the next step. Accepts an optional async
 *   `validateFn` that must resolve to `true` to allow navigation.
 * @property {Function} goBack - Go back to the previous step.
 * @property {Function} reset - Reset to step 0 and call `onReset` if provided.
 */

/**
 * Manages navigation state for a multi-step dialog.
 *
 * @param {Object} params
 * @param {number} params.totalSteps - Total number of steps (may be dynamic).
 * @param {Function} [params.onReset] - Called when `reset()` is invoked (e.g. resetForm).
 * @return {UseMultiStepDialogReturn}
 */
export const useMultiStepDialog = ({ totalSteps, onReset }) => {
  const [activeStep, setActiveStep] = useState(0);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === totalSteps - 1;

  const goToNext = useCallback(
    async (validateFn) => {
      if (typeof validateFn === "function") {
        const canProceed = await validateFn();
        if (!canProceed) return;
      }
      setActiveStep((s) => Math.min(s + 1, totalSteps - 1));
    },
    [totalSteps]
  );

  const goBack = useCallback(() => {
    setActiveStep((s) => Math.max(s - 1, 0));
  }, []);

  const reset = useCallback(() => {
    setActiveStep(0);
    onReset?.();
  }, [onReset]);

  return { activeStep, isFirstStep, isLastStep, goToNext, goBack, reset };
};
